import React, { Dispatch, FunctionComponent, SetStateAction, useState } from 'react';

import {
  ResponseCode,
  SiteGrp,
  TwoFactorAuthenticationEnabledLoginStates,
  TwoFactorAuthenticationMethod,
  TwoFactorAuthenticationMethods,
  UnauthenticatedTwoFactorAuthenticationVerifiedMethodData,
} from '@tradingblock/types';
import { Svg, TwoFactorAuthentication, useAuthentication } from '@tradingblock/components';
import {
  ISSUE_SENDING_TWO_FACTOR_AUTHENTICATION_SECURITY_CODE_ERROR_MSG,
  ISSUE_SUBMITTING_TWO_FACTOR_AUTHENTICATION_SECURITY_CODE_ERROR_MSG,
  TryLogin,
} from '@tradingblock/api';
import { config } from '../clientConfig';
import { getSubdirectory } from '../utils';

interface TwoFactorAuthenticationWrapperProps {
  password: string;
  username: string;
  setPassword: Dispatch<SetStateAction<string | null>>;
  setUsername: Dispatch<SetStateAction<string | undefined>>;
  verifiedTwoFactorAuthenticationMethodsData: UnauthenticatedTwoFactorAuthenticationVerifiedMethodData[];
  handleTryLoginSuccessWithToken: (token: string, responseCode?: ResponseCode) => Promise<void>;
  setShowTwoFactorAuthenticationComponent: Dispatch<SetStateAction<boolean>>;
};

const TwoFactorAuthenticationWrapper: FunctionComponent<TwoFactorAuthenticationWrapperProps> = ({
  password,
  username,
  setPassword,
  setUsername,
  verifiedTwoFactorAuthenticationMethodsData,
  handleTryLoginSuccessWithToken,
  setShowTwoFactorAuthenticationComponent,
}) => {
  const [plivoUuid, setPlivoUuid] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [{ isAuthenticating }, { setIsAuthenticating }] = useAuthentication();

  const handleCancel = () => {
    setPassword(null);
    setUsername(undefined);
    setShowTwoFactorAuthenticationComponent(false);
  };

  const subdirectory = getSubdirectory();

  const requestSecurityCode = (selectedVerifiedTwoFactorAuthenticationMethod: TwoFactorAuthenticationMethod) => {
    setErrorMsg(null);

    TryLogin(
      {
        password,
        username,
        twoFactorAuthenticationEnabledLoginState: TwoFactorAuthenticationEnabledLoginStates.RequestSecurityCode,
        twoFaType: selectedVerifiedTwoFactorAuthenticationMethod,
        siteGrp: config.siteGrp as SiteGrp,
      },
      {
        subdirectory,
      }
    ).then(({ plivoUuid, success, reason }) => {
      if (success) {
        if (selectedVerifiedTwoFactorAuthenticationMethod === TwoFactorAuthenticationMethods.sms) {
          if (plivoUuid) {
            setPlivoUuid(plivoUuid);
          } else {
            throw new Error(ISSUE_SENDING_TWO_FACTOR_AUTHENTICATION_SECURITY_CODE_ERROR_MSG);
          }
        }
      } else {
        throw reason;
      }
    }).catch((error) => {
      if (typeof error === 'string' && error) {
        setErrorMsg(error);
      } else if (typeof error === 'object' && error && error.message) {
        setErrorMsg(error.message);
      } else {
        setErrorMsg(ISSUE_SENDING_TWO_FACTOR_AUTHENTICATION_SECURITY_CODE_ERROR_MSG)
      }
    });
  };

  const submitSecurityCode = (
    selectedVerifiedTwoFactorAuthenticationMethod: TwoFactorAuthenticationMethod,
    twoFactorAuthenticationSecurityCode: string,
    plivoUuid?: string,
  ) => {
    if (isAuthenticating) {
      return;
    }

    setErrorMsg(null);
    setIsAuthenticating(true);

    TryLogin(
      {
        username,
        password,
        twoFactorAuthenticationEnabledLoginState: TwoFactorAuthenticationEnabledLoginStates.SubmitSecurityCode,
        twoFaSecurityCode: twoFactorAuthenticationSecurityCode,
        twoFaType: selectedVerifiedTwoFactorAuthenticationMethod,
        plivoUuid,
        siteGrp: config.siteGrp as SiteGrp,
      },
      {
        subdirectory,
      }
    ).then(async ({
      token,
      responseCode,
      reason,
      success,
    }) => {
      if (success) {
        if (token) {
          await handleTryLoginSuccessWithToken(token, responseCode);
        } else {
          throw new Error(ISSUE_SUBMITTING_TWO_FACTOR_AUTHENTICATION_SECURITY_CODE_ERROR_MSG);
        }
      } else {
        throw reason;
      }
    }).catch((error) => {
      if (typeof error === 'string' && error) {
        setErrorMsg(error);
      } else if (typeof error === 'object' && error && error.message) {
        setErrorMsg(error.message);
      } else {
        setErrorMsg(ISSUE_SUBMITTING_TWO_FACTOR_AUTHENTICATION_SECURITY_CODE_ERROR_MSG);
      }
    }).finally(() => {
      setIsAuthenticating(false);
    });
  };

  return (
    <>
      <TwoFactorAuthentication
        CancelSectionComponent={
          <p className="login-primary-2fa-cancel">
            Want to use a different account?{' '}
            <button
              type="button"
              onClick={handleCancel}
              className="login-primary-recovery-link login-primary-text-button"
            >
              Go back
            </button>
          </p>
        }
        requestSecurityCode={requestSecurityCode}
        submitSecurityCode={submitSecurityCode}
        verifiedTwoFactorAuthenticationMethodsData={verifiedTwoFactorAuthenticationMethodsData}
        primaryBtnText="Log in"
        primaryBtnClassName="btn btn-block btn-light btn-icon mt-3"
        PrimaryBtnIcon={<Svg path="icon-arrow" />}
        primaryBtnIconClassName="tb-icon"
        plivoUuid={plivoUuid}
        isSubmittingTwoFactorAuthenticationSecurityCode={isAuthenticating}
        errorMsg={errorMsg}
      />
    </>
  );
};

export default TwoFactorAuthenticationWrapper;
